import React, { useState } from "react";
import { Check, Zap, Crown, Rocket, Layers, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { handleStartEmpireFree, CTA_PRIMARY_FULL } from "@/lib/cta";

const TIERS = [
  {
    plan: "starter",
    name: "Starter",
    badge: "Indie",
    icon: Zap,
    blurb: "For the solo-founder who needs ClydeFlow doing the boring bits.",
    monthly: { id: "starter_monthly", price: "£9.99", cadence: "/ month" },
    yearly: { id: "starter_yearly", price: "£99", cadence: "/ year", save: "Save £20" },
    features: [
      "Content Engine — 20 posts / mo",
      "DM Closer — 1 account (Meta)",
      "Image Studio — 50 images / mo",
      "Assistant — 50 tasks / mo",
      "200 Tam tokens / mo",
      "Email support",
    ],
  },
  {
    plan: "pro",
    name: "Pro",
    badge: "Most Popular",
    icon: Rocket,
    blurb: "For operators ready to fully automate growth.",
    highlight: true,
    monthly: { id: "pro_monthly", price: "£29.99", cadence: "/ month" },
    yearly: { id: "pro_yearly", price: "£299", cadence: "/ year", save: "Save £60" },
    features: [
      "Content Engine — Unlimited",
      "DM Closer — 5 accounts, IG + FB",
      "Image Studio — 500 images / mo",
      "Mogul Sync — full network access",
      "1,000 Tam tokens / mo",
      "Priority support, custom branding",
    ],
  },
  {
    plan: "empire",
    name: "Empire",
    badge: "Agencies",
    icon: Layers,
    blurb: "Multi-brand. White-label. For agencies running 10+ accounts.",
    monthly: { id: "empire_monthly", price: "£99", cadence: "/ month" },
    yearly: { id: "empire_yearly", price: "£990", cadence: "/ year", save: "Save £198" },
    features: [
      "Everything in Pro",
      "25 connected accounts",
      "White-label Mogul Sync",
      "Image Studio — Unlimited",
      "5,000 Tam tokens / mo",
      "Dedicated success manager",
    ],
  },
];

const MOGUL = {
  plan: "mogul",
  name: "Mogul Growth Package",
  badge: "The Glasgow Deal",
  icon: Crown,
  blurb: "Done-for-you setup. We install the whole empire and hand you the keys.",
  oneTime: { id: "mogul", price: "£299", cadence: "one-time" },
  features: [
    "Everything in Pro for 6 months",
    "1:1 setup with the ClydeFlow team",
    "Mogul Sync local network onboarding",
    "Branded content templates",
    "Dedicated WhatsApp line",
    "3,000 Tam tokens included",
  ],
};

const WINGMAN_PACKS = [
  { id: "wingman_500", tokens: "500", price: "£9", per: "~£0.09 / msg" },
  { id: "wingman_2000", tokens: "2,000", price: "£29", per: "~£0.07 / msg" },
  { id: "wingman_10000", tokens: "10,000", price: "£99", per: "~£0.05 / msg" },
];

const Pricing = () => {
  const [billing, setBilling] = useState("yearly"); // "monthly" | "yearly"
  const [loadingId, setLoadingId] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const startCheckout = async (id) => {
    // No-card-required trial: if not logged in, route to Google sign-in FIRST.
    if (!user) {
      handleStartEmpireFree(user, navigate);
      return;
    }
    setLoadingId(id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: id,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Checkout could not be initiated.");
    } catch (err) {
      console.error(err);
      toast.error("Payment failed. Try again in a moment.");
    } finally {
      setLoadingId(null);
    }
  };

  const tierCta = (planId) => {
    if (!user) return CTA_PRIMARY_FULL;
    if (user.plan === planId) return "Current Plan";
    return `Upgrade to ${planId.charAt(0).toUpperCase() + planId.slice(1)}`;
  };

  return (
    <section id="pricing" className="relative py-24 lg:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400 mb-4">
            Pricing & Mogul Bundle
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05]">
            Pay for outcomes.
            <br />
            <span className="text-slate-500">Not promises.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            <span className="text-white font-semibold">Start free — no card required.</span>{" "}
            14 days to ship your empire. Trial includes 50 DMs and 2 AI blog
            posts on the house. Upgrade only when you're ready. The Mogul tier
            is a one-time British investment — that's the Glasgow way.
          </p>
        </div>

        {/* Monthly/Yearly toggle */}
        <div className="mb-12 flex items-center gap-3" data-testid="pricing-toggle">
          <button
            onClick={() => setBilling("monthly")}
            data-testid="pricing-toggle-monthly"
            className={[
              "px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] border transition-all rounded-sm",
              billing === "monthly"
                ? "bg-white text-[#050B14] border-white"
                : "bg-transparent text-slate-300 border-white/15 hover:border-white/30",
            ].join(" ")}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling("yearly")}
            data-testid="pricing-toggle-yearly"
            className={[
              "px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] border transition-all rounded-sm flex items-center gap-2",
              billing === "yearly"
                ? "bg-[#00F0FF] text-[#050B14] border-[#00F0FF]"
                : "bg-transparent text-slate-300 border-white/15 hover:border-white/30",
            ].join(" ")}
          >
            Yearly
            <span className="px-1.5 py-0.5 bg-emerald-400/20 text-emerald-300 text-[10px] rounded-sm">
              2 months free
            </span>
          </button>
        </div>

        {/* 3 subscription tiers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {TIERS.map((t) => {
            const Icon = t.icon;
            const variant = billing === "monthly" ? t.monthly : t.yearly;
            return (
              <div
                key={t.plan}
                data-testid={`pricing-card-${t.plan}`}
                className={[
                  "relative flex flex-col bg-[#0A192F] border rounded-sm p-8 transition-all duration-300",
                  t.highlight
                    ? "border-cyan-400/60 cf-glow lg:-translate-y-3"
                    : "border-white/10 hover:border-white/30",
                ].join(" ")}
              >
                {t.badge && (
                  <span
                    data-testid={`pricing-badge-${t.plan}`}
                    className={[
                      "absolute -top-3 left-6 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.3em]",
                      t.highlight
                        ? "bg-[#00F0FF] text-[#050B14]"
                        : "bg-white/10 text-slate-300",
                    ].join(" ")}
                  >
                    {t.badge}
                  </span>
                )}

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 flex items-center justify-center border border-white/10 bg-[#050B14]">
                    <Icon size={18} className="text-cyan-300" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white tracking-tight">
                    {t.name}
                  </h3>
                </div>

                <p className="mt-4 text-slate-400 text-sm leading-relaxed">{t.blurb}</p>

                <div className="mt-8 flex items-baseline gap-2">
                  <span className="font-heading text-5xl lg:text-6xl font-black text-white tracking-tighter">
                    {variant.price}
                  </span>
                  <span className="text-slate-500 text-sm font-mono uppercase tracking-widest">
                    {variant.cadence}
                  </span>
                </div>
                {billing === "yearly" && variant.save && (
                  <p className="mt-1 text-xs font-mono text-emerald-300">{variant.save}</p>
                )}

                <ul className="mt-8 space-y-3 flex-grow">
                  {t.features.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-slate-300 text-sm"
                      data-testid={`pricing-feature-${t.plan}-${i}`}
                    >
                      <Check size={16} className="text-cyan-300 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`pricing-cta-${t.plan}`}
                  onClick={() => startCheckout(variant.id)}
                  disabled={loadingId === variant.id}
                  className={[
                    "mt-10 w-full py-4 font-bold text-sm uppercase tracking-wider rounded-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed",
                    t.highlight
                      ? "bg-[#00F0FF] text-[#050B14] hover:bg-[#33F3FF] shadow-[0_0_25px_rgba(0,240,255,0.4)]"
                      : "bg-white/5 border border-white/15 text-white hover:bg-white/10",
                  ].join(" ")}
                >
                  {loadingId === variant.id ? "Opening..." : tierCta(t.plan)}
                </button>
              </div>
            );
          })}
        </div>

        {/* Mogul tier — full-width banner */}
        <div
          className="mt-10 relative bg-gradient-to-br from-[#0A192F] via-[#0A192F] to-[#072028] border border-cyan-400/40 cf-glow rounded-sm p-8 lg:p-12 grid lg:grid-cols-3 gap-8 items-center"
          data-testid="pricing-card-mogul"
        >
          <span className="absolute -top-3 left-6 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.3em] bg-gradient-to-r from-cyan-400 to-emerald-400 text-[#050B14]">
            {MOGUL.badge}
          </span>
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <Crown size={22} className="text-cyan-300" />
              <h3 className="font-heading text-3xl lg:text-4xl font-bold text-white tracking-tight">
                {MOGUL.name}
              </h3>
            </div>
            <p className="text-slate-300 max-w-2xl">{MOGUL.blurb}</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {MOGUL.features.map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-slate-300 text-sm"
                  data-testid={`pricing-feature-mogul-${i}`}
                >
                  <Check size={14} className="text-cyan-300 mt-1 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="text-center lg:text-right">
            <div className="font-heading text-6xl lg:text-7xl font-black text-white tracking-tighter">
              £299
            </div>
            <p className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-6">
              One-time · No subscription
            </p>
            <button
              data-testid="pricing-cta-mogul"
              onClick={() => startCheckout("mogul")}
              disabled={loadingId === "mogul"}
              className="w-full lg:w-auto px-8 py-4 bg-[#00F0FF] text-[#050B14] font-bold text-sm uppercase tracking-wider rounded-sm hover:bg-[#33F3FF] shadow-[0_0_30px_rgba(0,240,255,0.5)] disabled:opacity-60"
            >
              {loadingId === "mogul" ? "Opening..." : "Get the Mogul Deal"}
            </button>
          </div>
        </div>

        {/* Tam add-on */}
        <div className="mt-16 pt-12 border-t border-white/10" data-testid="wingman-addon-section">
          <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em] mb-3">
            <Sparkles size={14} /> Tam Add-On
          </div>
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-5">
              <h3 className="font-heading text-3xl lg:text-4xl font-black text-white tracking-tighter leading-tight">
                Bolt on <span className="text-cyan-300">Tam</span>.
              </h3>
              <p className="mt-3 text-slate-300 max-w-md">
                Your AI coach. Pick a personality — Glasgow coach, strategic
                operator, or brutal growth hacker. Tokens never expire.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
              {WINGMAN_PACKS.map((p) => (
                <div
                  key={p.id}
                  className="bg-[#0A192F] border border-white/10 hover:border-cyan-400/40 p-5 rounded-sm transition-all"
                  data-testid={`wingman-pack-${p.id}`}
                >
                  <p className="font-heading text-3xl font-black text-white">{p.tokens}</p>
                  <p className="text-xs font-mono uppercase tracking-widest text-slate-500">
                    tokens
                  </p>
                  <p className="mt-3 text-cyan-300 font-bold text-xl">{p.price}</p>
                  <p className="text-[11px] font-mono text-slate-500">{p.per}</p>
                  <button
                    onClick={() => startCheckout(p.id)}
                    disabled={loadingId === p.id}
                    data-testid={`wingman-pack-cta-${p.id}`}
                    className="mt-4 w-full py-2.5 bg-white/5 border border-white/15 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-white/10 disabled:opacity-60"
                  >
                    {loadingId === p.id ? "..." : "Top up"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-10 text-center text-xs font-mono uppercase tracking-[0.25em] text-slate-500">
          Start free · No card required · Cancel any time · Secured by Stripe
        </p>
      </div>
    </section>
  );
};

export default Pricing;
